import { useEffect, useRef, useState } from 'react';
import useStyle from './index.styles';
import { Button, Modal, Carousel, MenuProps, Dropdown } from 'antd';
import { CarouselRef } from 'antd/lib/carousel';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../utils/hooks';
import {
  CircleChevronLeftSolid,
  CircleChevronRightSolid,
  Download,
  EllipsisVerticalSolid,
  XmarkSolid,
} from '../../utils/icons';

interface ExtendedCarouselRef extends CarouselRef {
  goTo: (slide: number) => void;
}

interface Props {
  open: boolean;
  close: () => void;
  files: { name: string; url: string }[];
  index: number | null;
  isVideo?: boolean;
}

const CarouselViewerModal = (props: Props) => {
  const { open, close, index, files, isVideo } = props;
  const classes = useStyle();
  const carouselRef = useRef<ExtendedCarouselRef>(null);
  const [fileInTheScreen, setFileInTheScreen] = useState<{ name: string; url: string } | null>(
    null
  );
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    const openSlider = () => {
      if (open && carouselRef?.current && index !== null) {
        carouselRef.current.goTo(index);
        setFileInTheScreen(files[index]);
        clearInterval(intervalId);
      }
    };

    if (open) intervalId = setInterval(openSlider, 100);

    return () => {
      clearInterval(intervalId);
    };
  }, [files, index, open]);

  const onClose = () => {
    setFileInTheScreen(null);
    close();
  };

  const getItemsToDropdown = (): MenuProps['items'] => [
    { label: <p>{t('common.options')}</p>, key: 0, type: 'group' },
    {
      label: (
        <a href={fileInTheScreen?.url} className="no-underline">
          <p>{t('common.download')}</p>
        </a>
      ),
      key: 1,
    },
  ];

  return (
    <Modal
      title={
        <div className="flex items-center justify-between">
          {!isMobile && (
            <>
              <span>{fileInTheScreen?.name}</span>
              <div className="flex items-center">
                <a href={fileInTheScreen?.url} className="no-underline">
                  <Button className="flex items-center">
                    {t('common.download')} <Download className="ml-[8px]" />
                  </Button>
                </a>
                <div className="[&>svg>path]:fill-bg4">
                  <XmarkSolid onClick={onClose} className="ml-[16px] cursor-pointer" />
                </div>
              </div>
            </>
          )}
          {isMobile && (
            <>
              <Dropdown
                menu={{ items: getItemsToDropdown() }}
                trigger={['click']}
                overlayClassName="dropdown-menu"
                overlayStyle={{ minWidth: '124px' }}
              >
                <div className="[&>svg>path]:fill-bg4">
                  <EllipsisVerticalSolid onClick={(e) => e.preventDefault()} />
                </div>
              </Dropdown>
              <div className="[&>svg>path]:fill-bg4">
                <XmarkSolid onClick={close} />
              </div>
            </>
          )}
        </div>
      }
      open={open}
      onOk={close}
      onCancel={close}
      wrapClassName={classes.carouselViewerModal}
      width="100vw"
      footer={null}
      closeIcon={null}
      destroyOnClose
    >
      <Carousel
        className={classes.carousel}
        arrows
        effect="scrollx"
        nextArrow={isMobile ? undefined : <CircleChevronRightSolid />}
        prevArrow={isMobile ? undefined : <CircleChevronLeftSolid />}
        ref={carouselRef}
        afterChange={(current) => setFileInTheScreen(files[current])}
      >
        {files.map((file, index) => (
          <div key={`file-${index}`}>
            {isVideo ? (
              <video
                src={file.url}
                controls
                className="w-[100%]"
                controlsList="nodownload nofullscreen noremoteplayback"
                autoPlay
              />
            ) : (
              <img
                src={file.url}
                alt=""
                className="h-[100%] max-w-[calc(100vw-80px)] rounded-[8px] object-cover"
              />
            )}
          </div>
        ))}
      </Carousel>
    </Modal>
  );
};

export default CarouselViewerModal;
